import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import {
  BORDER_RADIUS,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_CARD,
  SPACING_CARD_BODY,
} from '../constants/layout';
import { FONT_TITLE3, FONT_BODY, FONT_FOOTNOTE } from '../constants/typography';

export default function EditNameModal({ isOpen, onClose, initialFirstName, initialLastName, onSaved }) {
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEditFirstName(initialFirstName || '');
      setEditLastName(initialLastName || '');
      setError('');
    }
  }, [isOpen, initialFirstName, initialLastName]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const trimmedFirst = editFirstName.trim();
    const trimmedLast = editLastName.trim();
    if (!trimmedFirst || !trimmedLast) {
      setError('First and last name are required.');
      return;
    }
    if (trimmedFirst === initialFirstName && trimmedLast === initialLastName) {
      onClose();
      return;
    }
    setSaving(true);
    setError('');
    try {
      const functions = getFunctions(getApp(), 'us-central1');
      const updateUserContactName = httpsCallable(functions, 'updateUserContactName');
      await updateUserContactName({ firstName: trimmedFirst, lastName: trimmedLast });
      onSaved(trimmedFirst, trimmedLast);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update name.');
    } finally {
      setSaving(false);
    }
  };

  const isValid = editFirstName.trim().length > 0 && editLastName.trim().length > 0;

  return (
    <div className="enm-overlay" onClick={() => !saving && onClose()}>
      <div className="enm-content" onClick={e => e.stopPropagation()}>
        <div className="enm-header">
          <h3>Edit Name</h3>
          <button className="enm-close" onClick={() => !saving && onClose()} disabled={saving}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="enm-body">
          <div className="enm-field">
            <label className="enm-label">First Name</label>
            <input type="text" value={editFirstName} onChange={e => setEditFirstName(e.target.value)}
              className="enm-input" placeholder="Required" autoComplete="given-name" disabled={saving} autoFocus />
          </div>
          <div className="enm-field">
            <label className="enm-label">Last Name</label>
            <input type="text" value={editLastName} onChange={e => setEditLastName(e.target.value)}
              className="enm-input" placeholder="Required" autoComplete="family-name" disabled={saving} />
          </div>
          {error && <p className="enm-error">{error}</p>}
        </div>
        <div className="enm-footer">
          <button onClick={() => onClose()} className="enm-cancel" disabled={saving}>Cancel</button>
          <button onClick={handleSave} className="enm-save" disabled={saving || !isValid}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <style jsx>{`
        .enm-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          z-index: 1100; padding: ${SPACING_CARD};
        }
        .enm-content {
          background-color: var(--color-background, #fff);
          border-radius: ${BORDER_RADIUS};
          max-width: 420px; width: 100%;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
        }
        .enm-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
          border-bottom: 1px solid var(--color-border);
        }
        .enm-header h3 {
          font-size: ${FONT_TITLE3}; font-weight: 600; margin: 0;
          color: var(--color-text-primary);
        }
        .enm-close {
          background: none; border: none; cursor: pointer;
          padding: 4px; color: var(--color-text-secondary);
        }
        .enm-close:hover { color: var(--color-text-primary); }
        .enm-body { padding: ${SPACING_CARD_BODY}; }
        .enm-field { margin-bottom: ${SPACING_CARD}; }
        .enm-field:last-child { margin-bottom: 0; }
        .enm-label {
          display: block; font-size: ${FONT_FOOTNOTE}; font-weight: 600;
          color: var(--color-text-primary); margin-bottom: ${SPACING_ELEMENT};
        }
        .enm-input {
          width: 100%; padding: ${SPACING_COMPONENT};
          border: 1px solid var(--color-border); border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY}; color: var(--color-text-primary);
          background-color: var(--color-surface, #fff); box-sizing: border-box;
        }
        .enm-input:focus { outline: none; border-color: var(--color-accent); }
        .enm-input:disabled { opacity: 0.6; cursor: not-allowed; }
        .enm-error {
          color: #ef4444; font-size: ${FONT_FOOTNOTE}; margin: ${SPACING_COMPONENT} 0 0;
        }
        .enm-footer {
          display: flex; justify-content: flex-end; gap: ${SPACING_COMPONENT};
          padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
          border-top: 1px solid var(--color-border);
        }
        .enm-cancel, .enm-save {
          padding: ${SPACING_COMPACT} ${SPACING_CARD}; border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY}; font-weight: 600; cursor: pointer;
        }
        .enm-cancel {
          background-color: transparent; color: var(--color-text-secondary);
          border: 1px solid var(--color-border);
        }
        .enm-cancel:hover:not(:disabled) { background-color: var(--color-surface-muted); }
        .enm-save { background-color: var(--color-accent); color: var(--color-accent-text); }
        .enm-save:hover:not(:disabled) { background-color: var(--color-accent-hover); }
        .enm-save:disabled, .enm-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
